import React, { useState, useEffect } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import { Modal, Table, notification, Row, Col, Divider } from 'antd';
import { ColumnsType } from 'antd/es/table';

import {
    LoadingOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    CheckOutlined,
} from '@ant-design/icons';

import Toolbar from '@controls/toolbar/toolbar';

import IBaseComponentProps from '@components/base-component-props';
import CountryItems from '@components/country-items/country-items';

import AirCargoCarriers from '@flc/components/countries/air-cargo-carriers';

import { exception, securityRestriction } from '@extensions/notification';
import { Permission, hasPermission } from '@enums/permission';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { ICountry } from '@entities/country';

const Countries = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const navigate = useNavigate();
    const resolved = useResolvedPath('');
    const [api, contextHolder] = notification.useNotification();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [selectedCountryIds, setSelectedCountryIds] = useState<React.Key[]>([]);
    const [currentCountry, setCurrentCountry] = useState<ICountry>();
    const [refreshRequired, setRefreshRequired] = useState(true);
    const [loading, setLoading] = useState(false);
    const [modal, modalContextHolder] = Modal.useModal();

    const [isViewOnly] = useState<boolean>(!hasPermission(userSession.permissions, Permission.ManageCountries));

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);
        setCountries([]);
        setLoading(true);

        serverFetch('countries', { method: 'GET' })
            .then((data: Array<ICountry>) => {
                setCountries(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    const onDelete = () => {
        if (!currentCountry) return;

        serverFetch(`countries/${currentCountry.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления стран', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderCountryToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить',
                        key: 'add',
                        type: 'primary',
                        disabled: loading || isViewOnly,
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCountries)) {
                                securityRestriction(api, [Permission.ManageCountries]);
                                return;
                            }

                            navigate(`${resolved.pathname}/new`);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentCountry || isViewOnly,
                        icon: <EditOutlined />,
                        onClick: () => {
                            navigate(`${resolved.pathname}/${currentCountry?.id}`);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentCountry || isViewOnly,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteCountry)) {
                                securityRestriction(api, [Permission.DeleteCountry]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить страну "${currentCountry?.name}"?`,
                                okType: 'primary',
                                icon: <QuestionCircleOutlined />,
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedCountryIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            let entity = countries.find((o) => o.id == selectedRowKeys[0]);
            setCurrentCountry(entity);
        } else {
            setCurrentCountry(undefined);
        }
    };

    const renderCountriesTable = () => {
        const columns: ColumnsType<ICountry> = [
            {
                title: '#',
                dataIndex: 'sequence',
                align: 'center',
                width: 60,
            },
            {
                title: 'Название',
                dataIndex: 'name',
                width: 160,
            },
            {
                title: 'Код',
                align: 'center',
                dataIndex: 'code',
                width: 80,
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                        background: record.color,
                        color: record.color ? '#ffffff' : '#000000',
                    },
                }),
            },
            {
                title: 'Аэропорт',
                align: 'center',
                dataIndex: 'airportCode',
                width: 80,
            },
            {
                title: 'Генерирация тарифа',
                align: 'center',
                width: 180,
                render: (_, record) => {
                    return record.generateTariff ? <CheckOutlined /> : '';
                },
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedCountryIds,
                    onChange: onSelectChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={countries}
                loading={tableLoading}
                onRow={(record: ICountry) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - 175px)` }}
            />
        );
    };

    return (
        <Row>
            <Col span={10}>
                {renderCountryToolbar()}
                {renderCountriesTable()}
                {contextHolder}
            </Col>
            {currentCountry && (
                <Col offset={1} span={13}>
                    <Divider titlePlacement='left'>Авиаперевозчики</Divider>
                    <AirCargoCarriers countryId={currentCountry?.id} api={api} serverFetch={serverFetch} />

                    <Divider titlePlacement='left'>Продукты</Divider>
                    <CountryItems countryId={currentCountry?.id} serverFetch={serverFetch} />
                </Col>
            )}

            {modalContextHolder}
        </Row>
    );
};

export default Countries;
